'use client'

import { useEffect, useRef, useState } from 'react'

const TWO_PI = Math.PI * 2
const HALF_PI = Math.PI * 0.5
const TIME_STEP = 1 / 60

interface Point {
  x: number
  y: number
}

const colors = ['#5CCBCB', '#5CCBCB', '#5CCBCB', '#3399FF', '#FFFFFF', '#FAB400', '#BA1F7E', '#F67C0F']
class ParticleClass {
  p0: Point
  p1: Point
  p2: Point
  p3: Point
  time: number
  duration: number
  color: string
  w: number
  h: number
  complete: boolean
  x: number
  y: number
  r: number
  sy: number

  constructor(p0: Point, p1: Point, p2: Point, p3: Point) {
    this.p0 = p0
    this.p1 = p1
    this.p2 = p2
    this.p3 = p3
    this.time = 0
    this.duration = 3 + Math.random() * 3
    this.color = colors[Math.floor(Math.random() * (colors.length - 1))]
    this.w = 10
    this.h = 6
    this.complete = false
    this.x = p0.x
    this.y = p0.y
    this.r = 0
    this.sy = 0
  }

  update() {
    this.time = Math.min(this.duration, this.time + TIME_STEP)
    const f = Ease.outCubic(this.time, 0, 1, this.duration)
    const p = cubeBezier(this.p0, this.p1, this.p2, this.p3, f)

    const dx = p.x - this.x
    const dy = p.y - this.y

    this.r = Math.atan2(dy, dx) + HALF_PI
    this.sy = Math.sin(Math.PI * f * 10)
    this.x = p.x
    this.y = p.y

    this.complete = this.time === this.duration
  }

  draw(ctx: CanvasRenderingContext2D) {
    ctx.save()
    ctx.translate(this.x, this.y)
    ctx.rotate(this.r)
    ctx.scale(1, this.sy)

    ctx.fillStyle = this.color
    ctx.fillRect(-this.w * 0.5, -this.h * 0.5, this.w, this.h)

    ctx.restore()
  }
}

class LoaderClass {
  x: number
  y: number
  r: number
  _progress: number
  complete: boolean

  constructor(x: number, y: number) {
    this.x = x
    this.y = y
    this.r = 24
    this._progress = 0
    this.complete = false
  }

  reset() {
    this._progress = 0
    this.complete = false
  }

  set progress(p: number) {
    this._progress = p < 0 ? 0 : p > 1 ? 1 : p
    this.complete = this._progress === 1
  }

  get progress() {
    return this._progress
  }

  draw(ctx: CanvasRenderingContext2D) {
    ctx.fillStyle = '#00000000'
    ctx.beginPath()
    ctx.arc(this.x, this.y, this.r, -HALF_PI, TWO_PI * this._progress - HALF_PI)
    ctx.lineTo(this.x, this.y)
    ctx.closePath()
    ctx.fill()
  }
}

class ExploaderClass {
  x: number
  y: number
  startRadius: number
  time: number
  duration: number
  progress: number
  complete: boolean

  constructor(x: number, y: number) {
    this.x = x
    this.y = y
    this.startRadius = 24
    this.time = 0
    this.duration = 0.4
    this.progress = 0
    this.complete = false
  }

  reset() {
    this.time = 0
    this.progress = 0
    this.complete = false
  }

  update() {
    this.time = Math.min(this.duration, this.time + TIME_STEP)
    this.progress = Ease.inBack(this.time, 0, 1, this.duration, 1.70158)
    this.complete = this.time === this.duration
  }

  draw(ctx: CanvasRenderingContext2D) {
    ctx.fillStyle = '#000'
    ctx.beginPath()
    ctx.arc(this.x, this.y, this.startRadius * (1 - this.progress), 0, TWO_PI)
    ctx.fill()
  }
}

const Ease = {
  inCubic: (t: number, b: number, c: number, d: number) => {
    t /= d
    return c * t * t * t + b
  },
  outCubic: (t: number, b: number, c: number, d: number) => {
    t /= d
    t--
    return c * (t * t * t + 1) + b
  },
  inOutCubic: (t: number, b: number, c: number, d: number) => {
    t /= d / 2
    if (t < 1) return (c / 2) * t * t * t + b
    t -= 2
    return (c / 2) * (t * t * t + 2) + b
  },
  inBack: (t: number, b: number, c: number, d: number, s: number) => {
    return c * (t /= d) * t * ((s + 1) * t - s) + b
  },
}

function cubeBezier(p0: Point, c0: Point, c1: Point, p1: Point, t: number): Point {
  const nt = 1 - t
  return {
    x: nt * nt * nt * p0.x + 3 * nt * nt * t * c0.x + 3 * nt * t * t * c1.x + t * t * t * p1.x,
    y: nt * nt * nt * p0.y + 3 * nt * nt * t * c0.y + 3 * nt * t * t * c1.y + t * t * t * p1.y,
  }
}

interface ConfettiProps {
  width?: number
  height?: number
  delay?: number
  loop?: boolean
}

export const Confetti = ({ width = 512, height = 350, delay = 1500, loop = false }: ConfettiProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [phase, setPhase] = useState(2)
  const [visible, setVisible] = useState(false)
  const animationRef = useRef<number>(null)

  const loaderRef = useRef<LoaderClass>(null)
  const exploaderRef = useRef<ExploaderClass>(null)
  const particlesRef = useRef<ParticleClass[]>([])

  useEffect(() => {
    setTimeout(() => {
      setVisible(true)
    }, delay)
  }, [delay])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Initialize objects
    loaderRef.current = new LoaderClass(width * 0.5, height * 0.5)
    exploaderRef.current = new ExploaderClass(width * 0.5, height * 0.5)
    createParticles()

    function createParticles() {
      particlesRef.current = []
      for (let i = 0; i < 128; i++) {
        const p0 = { x: width * 0.5, y: height * 0.5 }
        const p1 = { x: Math.random() * width, y: Math.random() * height }
        const p2 = { x: Math.random() * width, y: Math.random() * height }
        const p3 = { x: Math.random() * width, y: height + 64 }

        particlesRef.current.push(new ParticleClass(p0, p1, p2, p3))
      }
    }

    function update() {
      if (!loaderRef.current || !exploaderRef.current) return

      switch (phase) {
        case 0:
          loaderRef.current.progress += 1 / 45
          break
        case 1:
          exploaderRef.current.update()
          break
        case 2:
          particlesRef.current.forEach((p) => p.update())
          break
      }
    }

    function draw() {
      if (!ctx || !loaderRef.current || !exploaderRef.current) return

      ctx.clearRect(0, 0, width, height)

      switch (phase) {
        case 0:
          loaderRef.current.draw(ctx)
          break
        case 1:
          exploaderRef.current.draw(ctx)
          break
        case 2:
          particlesRef.current.forEach((p) => p.draw(ctx))
          break
      }
    }

    function checkParticlesComplete() {
      return particlesRef.current.every((p) => p.complete)
    }

    function animate() {
      update()
      draw()

      if (phase === 0 && loaderRef.current?.complete) {
        setPhase(2)
      } else if (phase === 1 && exploaderRef.current?.complete) {
        setPhase(2)
      } else if (phase === 2 && checkParticlesComplete()) {
        loaderRef.current?.reset()
        exploaderRef.current?.reset()
        createParticles()
        setPhase(loop ? 2 : 3)
      }

      animationRef.current = requestAnimationFrame(animate)
    }
    if (visible) {
      animate()
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [phase, width, height, visible, loop])

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{ width: '100%', height: '100%', maxWidth: '700px', maxHeight: '480px' }}
    />
  )
}
